import React, { useState } from 'react';
import {
  Shield, Smartphone, Lock, Settings, Monitor, Cloud,
  Users, CheckCircle, ArrowRight, ChevronDown, Factory,
  Truck, ShoppingCart, Briefcase, GraduationCap, Building2,
  Server, Key, FileCheck, Zap } from
'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import emailjs from '@emailjs/browser';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    deviceCount: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDemoRequest = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // EmailJS configuration from environment variables
    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    try {
      const templateParams = {
        to_email: 'sachin@gekonova.com',
        from_name: formData.name,
        from_company: formData.company,
        from_email: formData.email,
        from_phone: formData.phone,
        device_count: formData.deviceCount,
        message: `New demo request from ${formData.name} at ${formData.company}. They manage ${formData.deviceCount} devices. Contact: ${formData.email}, ${formData.phone}`
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);

      toast.success('Demo request submitted successfully! We\'ll contact you soon.');
      setFormData({ name: '', company: '', email: '', phone: '', deviceCount: '' });
    } catch (error) {
      console.error('EmailJS Error:', error);
      toast.error('Failed to send request. Please try again or email us directly at sachin@gekonova.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToDemo = () => {
    document.getElementById('demo-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/90 backdrop-blur-md z-50 border-b border-gray-100">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_307ce5d4-baf7-4bd0-bc82-92600e6e77f7/artifacts/ngmkfe2i_cropped-GEKOmdm3.jpg.webp" alt="GekoMDM Logo - Android Device Management" className="h-10" width="120" height="40" loading="eager" />
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-gray-700 hover:text-purple-700 transition-colors font-medium">Features</a>
            <a href="#use-cases" className="text-gray-700 hover:text-purple-700 transition-colors font-medium">Use Cases</a>
            <a href="#how-it-works" className="text-gray-700 hover:text-purple-700 transition-colors font-medium">How It Works</a>
            <a href="#faq" className="text-gray-700 hover:text-purple-700 transition-colors font-medium">FAQ</a>
            <Button onClick={scrollToDemo} className="bg-purple-700 hover:bg-purple-800 text-white">
              Request Demo
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-purple-50 via-white to-green-50">
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight mb-6">GekoMDM – Secure Mobile Device Management for Enterprise

              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                GekoMDM is an enterprise-grade Mobile Device Management solution for Android devices. Secure, scale, and streamline device management from a centralised cloud dashboard.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button onClick={scrollToDemo} size="lg" className="bg-purple-700 hover:bg-purple-800 text-white text-lg px-8 py-6">
                  Request Demo <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button onClick={scrollToDemo} size="lg" variant="outline" className="border-2 border-purple-700 text-purple-700 hover:bg-purple-50 text-lg px-8 py-6">
                  Talk to an Expert
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-purple-100 to-green-100 rounded-2xl p-8 h-96 flex items-center justify-center overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1607252650355-f7fd0460ccdb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxhbmRyb2lkJTIwc21hcnRwaG9uZSUyMHRhYmxldCUyMGRldmljZXMlMjBtdWx0aXBsZSUyMGVudGVycHJpc2V8ZW58MHx8fHwxNzcwNjE4MzM4fDA&ixlib=rb-4.1.0&q=85"
                  alt="Android Device Management - GekoMDM supports enterprise Android smartphones and tablets"
                  className="w-full h-full object-cover rounded-xl"
                  loading="eager"
                  width="600"
                  height="400" />

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="container mx-auto max-w-7xl text-center">
          <div className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full shadow-sm mb-8">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <span className="!font-semibold text-gray-700">A Gekonova Product</span>
          </div>
          <p className="text-gray-600 max-w-3xl mx-auto mb-12">
            Enterprise-ready, scalable, and secure. Trusted by organisations managing thousands of Android devices across multiple industries.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="flex flex-col items-center">
              <Shield className="h-12 w-12 text-purple-700 mb-3" />
              <span className="font-semibold text-gray-900">Enterprise Security</span>
            </div>
            <div className="flex flex-col items-center">
              <Cloud className="h-12 w-12 text-green-600 mb-3" />
              <span className="font-semibold text-gray-900">Cloud-Based</span>
            </div>
            <div className="flex flex-col items-center">
              <Zap className="h-12 w-12 text-purple-700 mb-3" />
              <span className="font-semibold text-gray-900">Scalable</span>
            </div>
            <div className="flex flex-col items-center">
              <FileCheck className="h-12 w-12 text-green-600 mb-3" />
              <span className="font-semibold text-gray-900">Compliance Ready</span>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Managing Android Devices Shouldn't Be This Hard
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Organisations face critical challenges when managing large fleets of Android devices without proper tools.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
            { title: 'Security Risks', desc: 'Unmanaged devices expose sensitive data to breaches and unauthorised access' },
            { title: 'Manual Updates', desc: 'Time-consuming app installations and updates across hundreds of devices' },
            { title: 'Lack of Visibility', desc: 'No real-time insights into device status, location, or usage patterns' },
            { title: 'Downtime & Misuse', desc: 'Devices used for unauthorised purposes, leading to productivity loss' }].
            map((problem, idx) =>
            <Card key={idx} className="border-2 hover:border-purple-200 transition-all hover:shadow-lg">
                <CardContent className="pt-6">
                  <div className="h-12 w-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                    <div className="h-2 w-2 bg-red-600 rounded-full"></div>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{problem.title}</h3>
                  <p className="text-gray-600 text-sm">{problem.desc}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-20 px-6 bg-gradient-to-br from-purple-50 to-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                What is GekoMDM?
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                GekoMDM is a powerful Device Owner-based Android Mobile Device Management solution that gives you complete control over your device fleet from a centralised cloud dashboard.
              </p>
              <ul className="space-y-4">
                {[
                'Device Owner enrolment for maximum control',
                'Cloud-based centralised management',
                'Real-time monitoring and alerts',
                'Secure policy enforcement',
                'Offline-capable architecture'].
                map((item, idx) =>
                <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                )}
              </ul>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="space-y-6">
                <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-lg">
                  <Smartphone className="h-10 w-10 text-purple-700" />
                  <div>
                    <div className="font-semibold text-gray-900">Android Devices</div>
                    <div className="text-sm text-gray-600">All managed devices</div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <ArrowRight className="h-8 w-8 text-gray-400 rotate-90" />
                </div>
                <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg">
                  <Cloud className="h-10 w-10 text-green-600" />
                  <div>
                    <div className="font-semibold text-gray-900">GekoMDM Cloud</div>
                    <div className="text-sm text-gray-600">Centralized control</div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <ArrowRight className="h-8 w-8 text-gray-400 rotate-90" />
                </div>
                <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-lg">
                  <Monitor className="h-10 w-10 text-purple-700" />
                  <div>
                    <div className="font-semibold text-gray-900">Admin Dashboard</div>
                    <div className="text-sm text-gray-600">Monitor & control</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section id="features" className="py-20 px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Powerful Features for Complete Device Control
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to manage, secure, and optimise your Android device fleet.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
            { icon: Key, title: 'Device Owner Enrolment', desc: 'Zero-touch provisioning with QR codes and NFC for seamless device setup' },
            { icon: Lock, title: 'Kiosk Mode', desc: 'Single or multi-app kiosk mode to lock devices to specific applications' },
            { icon: Cloud, title: 'Remote App Management', desc: 'Install, update, and remove apps remotely across all devices' },
            { icon: Settings, title: 'Policy Management', desc: 'Enforce security policies, restrictions, and configurations centrally' },
            { icon: Monitor, title: 'Real-time Monitoring', desc: 'Track device health, location, battery, and usage in real-time' },
            { icon: Server, title: 'Offline Support', desc: 'Sync-friendly architecture that works even with intermittent connectivity' },
            { icon: Users, title: 'Role-based Access', desc: 'Granular admin permissions and multi-level access control' },
            { icon: Shield, title: 'Security & Compliance', desc: 'Enterprise-grade encryption and compliance-ready architecture' }].
            map((feature, idx) =>
            <Card key={idx} className="hover:shadow-xl transition-all hover:-translate-y-1 border-2 hover:border-purple-200">
                <CardContent className="pt-6">
                  <div className="h-14 w-14 bg-gradient-to-br from-purple-100 to-green-100 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="h-7 w-7 text-purple-700" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.desc}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              How GekoMDM Works
            </h2>
            <p className="text-xl text-gray-600">
              Get started in three simple steps
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
            {
              step: '01',
              title: 'Enrol Devices',
              desc: 'Use zero-touch provisioning, QR codes, or manual enrolment to onboard devices into GekoMDM with Device Owner mode',
              icon: Smartphone
            },
            {
              step: '02',
              title: 'Apply Policies',
              desc: 'Configure security policies, install apps, set restrictions, and customise device behaviour from the central dashboard',
              icon: Settings
            },
            {
              step: '03',
              title: 'Monitor & Control',
              desc: 'Track device status in real-time, push updates remotely, and maintain complete control over your fleet',
              icon: Monitor
            }].
            map((step, idx) =>
            <div key={idx} className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="text-6xl font-bold text-purple-100 mb-4">{step.step}</div>
                  <step.icon className="h-12 w-12 text-purple-700 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.desc}</p>
                </div>
                {idx < 2 &&
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="h-8 w-8 text-purple-300" />
                  </div>
              }
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Industry Use Cases */}
      <section id="use-cases" className="py-20 px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Trusted Across Industries
            </h2>
            <p className="text-xl text-gray-600">
              GekoMDM powers device management for diverse enterprise needs
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
            { icon: Factory, title: 'Manufacturing', desc: 'Manage production line tablets and handheld devices with kiosk mode and app restrictions' },
            { icon: Truck, title: 'Logistics & Fleet', desc: 'Track delivery devices, enforce route compliance, and monitor driver device usage' },
            { icon: ShoppingCart, title: 'Retail POS', desc: 'Secure point-of-sale devices, manage inventory apps, and prevent unauthorised usage' },
            { icon: Briefcase, title: 'Field Force', desc: 'Equip field teams with managed devices for secure data access and real-time updates' },
            { icon: GraduationCap, title: 'Education', desc: 'Deploy tablets for students with content filtering and app management' },
            { icon: Building2, title: 'Government', desc: 'Compliance-ready device management for public sector and government agencies' }].
            map((useCase, idx) =>
            <Card key={idx} className="hover:shadow-xl transition-all hover:-translate-y-1 border-2 hover:border-green-200">
                <CardContent className="pt-6">
                  <div className="h-14 w-14 bg-gradient-to-br from-green-100 to-purple-100 rounded-xl flex items-center justify-center mb-4">
                    <useCase.icon className="h-7 w-7 text-green-700" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{useCase.title}</h3>
                  <p className="text-gray-600">{useCase.desc}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Why GekoMDM */}
      <section className="py-20 px-6 bg-gradient-to-br from-green-50 to-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Choose GekoMDM?
            </h2>
            <p className="text-xl text-gray-600">
              Built for enterprises, backed by innovation
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {[
            { title: 'Built for Scalability', desc: 'Manage 10 or 10,000 devices with the same ease. Our architecture scales with your business growth.' },
            { title: 'Reliable Offline Handling', desc: 'Sync-capable design ensures policy enforcement even when devices are temporarily offline.' },
            { title: 'Enterprise-Grade Security', desc: 'End-to-end encryption, secure APIs, and compliance-ready infrastructure.' },
            { title: 'Backed by Gekonova', desc: 'Built in partnership with Gekonova, ensuring quality, reliability, and continuous innovation.' },
            { title: 'Cost-Effective', desc: 'Competitive pricing compared to global MDM tools, without compromising on features.' },
            { title: 'Customisable & Extensible', desc: 'APIs and webhooks for custom integrations with your existing enterprise systems.' }].
            map((reason, idx) =>
            <div key={idx} className="flex gap-4 p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{reason.title}</h3>
                  <p className="text-gray-600">{reason.desc}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Security & Compliance */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="bg-gradient-to-br from-purple-700 to-green-600 rounded-3xl p-12 text-white">
            <div className="text-center mb-12">
              <Shield className="h-16 w-16 mx-auto mb-6" />
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Security & Compliance First
              </h2>
              <p className="text-xl text-purple-100 max-w-3xl mx-auto">
                Your data security and compliance requirements are our top priority
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
              { title: 'Data Encryption', desc: 'AES-256 encryption for data at rest and in transit' },
              { title: 'Secure Communication', desc: 'TLS/SSL protocols for all API communications' },
              { title: 'Device Restrictions', desc: 'Granular control over device features and permissions' },
              { title: 'Compliance Ready', desc: 'Built to meet enterprise compliance standards' }].
              map((item, idx) =>
              <div key={idx} className="text-center">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                    <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                    <p className="text-sm text-purple-100">{item.desc}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="demo-section" className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Take Control of Your Android Devices Today
            </h2>
            <p className="text-xl text-gray-600">
              See GekoMDM in action. Request a personalised demo and discover how we can transform your device management.
            </p>
          </div>
          <Card className="shadow-2xl border-2 border-purple-100">
            <CardContent className="pt-8">
              <form onSubmit={handleDemoRequest} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                    <Input
                      type="text"
                      name="name"
                      placeholder="John Doe"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="h-12" />

                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Company Name *</label>
                    <Input
                      type="text"
                      name="company"
                      placeholder="Acme Corporation"
                      required
                      value={formData.company}
                      onChange={handleInputChange}
                      className="h-12" />

                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Work Email *</label>
                    <Input
                      type="email"
                      name="email"
                      placeholder="john@company.com"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="h-12" />

                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                    <Input
                      type="tel"
                      name="phone"
                      placeholder="+1 (555) 000-0000"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="h-12" />

                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Number of Devices *</label>
                  <Input
                    type="text"
                    name="deviceCount"
                    placeholder="e.g., 50-100 devices"
                    required
                    value={formData.deviceCount}
                    onChange={handleInputChange}
                    className="h-12" />

                </div>
                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-purple-700 hover:bg-purple-800 text-white text-lg py-6"
                  disabled={isSubmitting}>

                  {isSubmitting ? 'Submitting...' : 'Request Demo'}
                  {!isSubmitting && <ArrowRight className="ml-2 h-5 w-5" />}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about GekoMDM
            </p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="bg-white rounded-lg px-6 border-2">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-purple-700">
                What Android devices are supported by GekoMDM?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                GekoMDM supports all Android devices running Android 5.0 (Lollipop) and above. For Device Owner mode features, Android 6.0+ is recommended. We support smartphones, tablets, rugged devices, and specialized Android hardware.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-2" className="bg-white rounded-lg px-6 border-2">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-purple-700">
                Is GekoMDM based on Device Owner mode?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                Yes, GekoMDM leverages Android's Device Owner mode (also known as Fully Managed Device mode) to provide the highest level of device control and security. This allows for complete policy enforcement, kiosk mode, and advanced restrictions that aren't possible with standard device administration.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-3" className="bg-white rounded-lg px-6 border-2">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-purple-700">
                Can GekoMDM work offline or with intermittent connectivity?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                Absolutely. GekoMDM is designed with offline capability in mind. Policies remain enforced even when devices lose connectivity, and changes sync automatically when connection is restored. This makes it ideal for field operations and areas with limited network coverage.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-4" className="bg-white rounded-lg px-6 border-2">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-purple-700">
                Is GekoMDM suitable for large enterprises managing thousands of devices?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                Yes, GekoMDM is built for enterprise scalability. Our cloud infrastructure can handle fleets ranging from 10 to 10,000+ devices without performance degradation. We offer bulk operations, group management, and powerful filtering to efficiently manage large deployments.
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="item-5" className="bg-white rounded-lg px-6 border-2">
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-purple-700">
                How is GekoMDM different from other MDM tools?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                GekoMDM combines enterprise-grade features with cost-effectiveness and flexibility. Unlike legacy MDM solutions, we offer modern cloud architecture, offline resilience, intuitive UI, and competitive pricing. Built in partnership with Gekonova, we provide local support and customization options that global vendors can't match.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src="https://customer-assets.emergentagent.com/job_307ce5d4-baf7-4bd0-bc82-92600e6e77f7/artifacts/ngmkfe2i_cropped-GEKOmdm3.jpg.webp" alt="GekoMDM - Enterprise Android MDM Solution by Gekonova" className="h-10 mb-4" width="120" height="40" loading="lazy" />
              <p className="text-gray-400 text-sm">
                Enterprise Android Device Management, Built with Gekonova
              </p>
            </div>
            <div>
              <h3 className="font-bold mb-4">Product</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#use-cases" className="hover:text-white transition-colors">Use Cases</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a></li>
                <li><a href="#demo-section" className="hover:text-white transition-colors">Request Demo</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Company</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="https://www.gekonova.com/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">About Gekonova</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact Us</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Contact</h3>
              <p className="text-gray-400 text-sm mb-2">For inquiries and support:</p>
              <a href="mailto:info@gekomdm.com" className="text-purple-400 hover:text-purple-300 transition-colors">mark@gekonova.com

              </a>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>© 2026 GekoMDM. All rights reserved. Powered by Gekonova.</p>
          </div>
        </div>
      </footer>
    </div>);

};

export default Home;